import { faBox, faEdit, faPlus, faTrash, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Card from 'common/card/Card';
import CardBody from 'common/card/CardBody';
import CardHead from 'common/card/CardHead';
import Modal from 'common/modal/Modal';
import ModalContent from 'common/modal/ModalContent';
import ModalClose from 'common/modal/ModalClose';
import { NotificationContext } from './Notification';
import { AuthenticationContext } from './providers/AuthCtx';
import { PartOrder } from 'data/part_order';
import { getAllPartOrders, deletePartOrder } from 'services/part_order_service';
import { handleError } from 'services/api';
import { ApiError } from 'data/api_error';
import { Message } from 'data/message';
import moment from 'moment';

const PartOrders: React.FC = () => {
    const authCtx = useContext(AuthenticationContext);
    const notificationHandler = useContext(NotificationContext);
    const navigate = useNavigate();
    const [partOrders, setPartOrders] = useState<PartOrder[]>([]);
    const [deleteCandidate, setDeleteCandidate] = useState<PartOrder | null>(null);
    const [searchOrderNum, setSearchOrderNum] = useState('');
    const [searchPhoneNum, setSearchPhoneNum] = useState('');

    const fetchPartOrders = useCallback(() => {
        getAllPartOrders(authCtx).then((data: PartOrder[]) => {
            setPartOrders(data);
        }).catch((e: ApiError) => {
            handleError(e, notificationHandler);
        });
    }, [authCtx]);

    useEffect(() => {
        if (!authCtx.session) {
            return;
        }

        fetchPartOrders();
    }, [fetchPartOrders, authCtx]);

    const filteredPartOrders = useMemo(() => {
        return partOrders.filter((po) => {
            const matchesOrderNum = searchOrderNum === '' || po.order_num.toLowerCase().includes(searchOrderNum.toLowerCase());
            const matchesPhoneNum = searchPhoneNum === '' || po.phone_no.includes(searchPhoneNum);
            return matchesOrderNum && matchesPhoneNum;
        });
    }, [partOrders, searchOrderNum, searchPhoneNum]);

    const handleDelete = useCallback(() => {
        if (!deleteCandidate) {
            return;
        }

        deletePartOrder(authCtx, deleteCandidate).then((res: Message) => {
            notificationHandler({
                type: "success",
                message: res.message,
                timeout: 4000,
            });
            setDeleteCandidate(null);
            fetchPartOrders();
        }).catch((e: ApiError) => {
            handleError(e, notificationHandler);
        });
    }, [authCtx, deleteCandidate, notificationHandler, fetchPartOrders]);

    return (
        <>
            <div
                className="container"
                style={{
                    gridTemplateColumns: "15% 70% 15%",
                    marginTop: "5%",
                }}
            >
                <div style={{ gridArea: "card" }}>
                    <Card>
                        <CardHead>
                            <FontAwesomeIcon icon={faBox}></FontAwesomeIcon>
                        </CardHead>
                        <CardBody>
                            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                                <div style={{ display: 'flex', gap: '0.5rem', flex: 1 }}>
                                    <TextField
                                        placeholder="N° de commande"
                                        variant="standard"
                                        value={searchOrderNum}
                                        onChange={(e) => setSearchOrderNum(e.target.value)}
                                        InputProps={{
                                            startAdornment: <FontAwesomeIcon icon={faSearch} style={{ marginRight: '0.5rem', color: 'var(--color-secondary)' }} />
                                        }}
                                        style={{ flex: 1 }}
                                    />
                                    <TextField
                                        placeholder="Numéro de téléphone"
                                        variant="standard"
                                        value={searchPhoneNum}
                                        onChange={(e) => setSearchPhoneNum(e.target.value)}
                                        InputProps={{
                                            startAdornment: <FontAwesomeIcon icon={faSearch} style={{ marginRight: '0.5rem', color: 'var(--color-secondary)' }} />
                                        }}
                                        style={{ flex: 1 }}
                                    />
                                </div>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                                    onClick={() => navigate('/part-orders/create')}
                                >
                                    Nouvelle commande
                                </Button>
                            </div>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>N° de commande</TableCell>
                                            <TableCell>Téléphone</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Date de vente</TableCell>
                                            <TableCell>Date réception pièce</TableCell>
                                            <TableCell>Date dernier appel</TableCell>
                                            <TableCell>Confirmé</TableCell>
                                            <TableCell>Langue</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredPartOrders.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={9} align="center">
                                                    {partOrders.length === 0 ? 'Aucune commande trouvée' : 'Aucun résultat pour cette recherche'}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredPartOrders.map((po) => (
                                                <TableRow key={po.id}>
                                                    <TableCell>{po.order_num}</TableCell>
                                                    <TableCell>{po.phone_no}</TableCell>
                                                    <TableCell>{po.type === 'P' ? 'Pièce' : 'Filtre à eau'}</TableCell>
                                                    <TableCell>{moment.utc(po.sell_date).format('YYYY-MM-DD')}</TableCell>
                                                    <TableCell>{po.date_received ?  moment.utc(po.date_received).format('YYYY-MM-DD') : '-'}</TableCell>
                                                    <TableCell>{po.last_call_date ?  moment.utc(po.last_call_date).local().format('YYYY-MM-DD HH:mm') : '-'}</TableCell>
                                                    <TableCell>{po.confirmed ? 'Oui' : 'Non'}</TableCell>
                                                    <TableCell>{po.lang === 'fr' ? 'Français' : 'Anglais'}</TableCell>
                                                    <TableCell align="right">
                                                        <Button
                                                            color="info"
                                                            size="small"
                                                            startIcon={<FontAwesomeIcon icon={faEdit} />}
                                                            component={Link}
                                                            to={`/part-orders/${po.id}/edit`}
                                                            >
                                                        </Button>
                                                        <Button
                                                            color="error"
                                                            size="small"
                                                            startIcon={<FontAwesomeIcon icon={faTrash} />}
                                                            onClick={() => setDeleteCandidate(po)}
                                                            >
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardBody>
                    </Card>
                </div>
            </div>
            {deleteCandidate && (
                <Modal onClick={() => setDeleteCandidate(null)}>
                    <ModalContent style={{ minWidth: '400px' }}>
                        <ModalClose onClick={() => setDeleteCandidate(null)} />
                        <h3>Confirmer la suppression</h3>
                        <p>
                            Êtes-vous sûr de vouloir supprimer la commande <strong>{deleteCandidate.order_num}</strong> ?
                        </p>
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleDelete}>
                                Supprimer
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setDeleteCandidate(null)}>
                                Annuler
                            </Button>
                        </div>
                    </ModalContent>
                </Modal>
            )}
        </>
    );
};

export default PartOrders;
